/**
 * Subscription Context
 * Global subscription state management with feature gating
 */

import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { 
  UserSubscription, 
  SubscriptionTier, 
  BillingCycle,
  UsageMetrics,
  TrialInfo
} from '../types/subscription';
import { 
  SUBSCRIPTION_LIMITS, 
  canAccessFeature, 
  isTierHigherOrEqual
} from '../data/subscriptionPlans';

interface SubscriptionContextType {
  subscription: UserSubscription | null;
  isLoading: boolean;
  error: string | null;
  
  // Subscription info
  currentTier: SubscriptionTier;
  isActive: boolean;
  isTrial: boolean;
  trialInfo: TrialInfo | null;
  
  // Feature access
  canAccess: (feature: keyof typeof SUBSCRIPTION_LIMITS.free.features) => boolean;
  hasFeature: (feature: string) => boolean;
  requiresTier: (tier: SubscriptionTier) => boolean;
  
  // Usage tracking
  usage: UsageMetrics;
  isWithinLimit: (limitType: string) => boolean;
  getRemainingLimit: (limitType: string) => number | 'unlimited';
  
  // Actions
  upgradeTo: (tier: SubscriptionTier, cycle: BillingCycle) => Promise<void>;
  downgradeTo: (tier: SubscriptionTier) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  reactivateSubscription: () => Promise<void>;
  startTrial: () => Promise<void>;
  updateBillingCycle: (cycle: BillingCycle) => Promise<void>;
  
  // Utilities
  getUpgradeUrl: (tier: SubscriptionTier) => string;
  showUpgradeModal: (feature?: string) => void;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
  initialSubscription?: UserSubscription;
}

export function SubscriptionProvider({ children, initialSubscription }: SubscriptionProviderProps) {
  // State
  const [subscription, setSubscription] = useState<UserSubscription | null>(
    initialSubscription || null
  );
  const [isLoading, setIsLoading] = useState(!initialSubscription);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<UsageMetrics>({
    projectsCreated: 0,
    calculationsRun: 0,
    pdfExports: 0,
    resourceDownloads: 0
  });

  // Initialize subscription (simulate API call)
  useEffect(() => {
    if (!initialSubscription) {
      loadSubscription();
    }
  }, [initialSubscription]);

  const loadSubscription = async () => {
    try {
      setIsLoading(true);
      
      // Simulate API call - In production, fetch from backend
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check localStorage for demo purposes
      const savedSub = localStorage.getItem('userSubscription');
      if (savedSub) {
        setSubscription(JSON.parse(savedSub));
      } else {
        // Default to free tier
        const defaultSubscription: UserSubscription = {
          id: 'sub_demo_' + Date.now(),
          userId: 'user_demo',
          tier: 'free',
          status: 'active',
          billingCycle: 'monthly',
          currentPeriodStart: new Date().toISOString(),
          currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          cancelAtPeriodEnd: false
        };
        setSubscription(defaultSubscription);
        localStorage.setItem('userSubscription', JSON.stringify(defaultSubscription));
      }
      
      // Load usage metrics
      const savedUsage = localStorage.getItem('usageMetrics');
      if (savedUsage) {
        setUsage(JSON.parse(savedUsage));
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to load subscription');
      console.error('Subscription load error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Computed values
  const currentTier: SubscriptionTier = subscription?.tier || 'free';
  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';
  const isTrial = subscription?.status === 'trialing';
  
  const trialInfo: TrialInfo | null = subscription?.trialEnd ? {
    isTrialing: isTrial,
    trialDaysRemaining: Math.ceil(
      (new Date(subscription.trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    ),
    trialEndDate: subscription.trialEnd,
    hasUsedTrial: false // Would check backend
  } : null;

  // Feature access functions
  const canAccess = (feature: keyof typeof SUBSCRIPTION_LIMITS.free.features): boolean => {
    if (!subscription) return false;
    return canAccessFeature(currentTier, feature);
  };

  const hasFeature = (feature: string): boolean => {
    const featureKey = feature as keyof typeof SUBSCRIPTION_LIMITS.free.features;
    return canAccess(featureKey);
  };

  const requiresTier = (tier: SubscriptionTier): boolean => {
    return isTierHigherOrEqual(currentTier, tier);
  };

  // Usage limit functions
  const isWithinLimit = (limitType: string): boolean => {
    const limits = SUBSCRIPTION_LIMITS[currentTier];
    const limitValue = limits[limitType as keyof typeof limits];
    
    if (limitValue === 'unlimited' || limitValue === -1) return true;
    
    const usageValue = usage[limitType as keyof UsageMetrics] || 0;
    return usageValue < (limitValue as number);
  };

  const getRemainingLimit = (limitType: string): number | 'unlimited' => {
    const limits = SUBSCRIPTION_LIMITS[currentTier];
    const limitValue = limits[limitType as keyof typeof limits];
    
    if (limitValue === 'unlimited' || limitValue === -1) return 'unlimited';
    
    const usageValue = usage[limitType as keyof UsageMetrics] || 0;
    return Math.max(0, (limitValue as number) - usageValue);
  };

  // Subscription actions
  const upgradeTo = async (tier: SubscriptionTier, cycle: BillingCycle): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Simulate API call to payment processor
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedSubscription: UserSubscription = {
        ...subscription!,
        tier,
        billingCycle: cycle,
        status: 'active',
        currentPeriodStart: new Date().toISOString(),
        currentPeriodEnd: new Date(
          Date.now() + (cycle === 'annual' ? 365 : 30) * 24 * 60 * 60 * 1000
        ).toISOString()
      };
      
      setSubscription(updatedSubscription);
      localStorage.setItem('userSubscription', JSON.stringify(updatedSubscription));
      setError(null);
    } catch (err) {
      setError('Failed to upgrade subscription');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const downgradeTo = async (tier: SubscriptionTier): Promise<void> => {
    try {
      setIsLoading(true);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedSubscription: UserSubscription = {
        ...subscription!,
        tier,
        cancelAtPeriodEnd: true
      };
      
      setSubscription(updatedSubscription);
      localStorage.setItem('userSubscription', JSON.stringify(updatedSubscription));
      setError(null);
    } catch (err) {
      setError('Failed to downgrade subscription');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const cancelSubscription = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedSubscription: UserSubscription = {
        ...subscription!,
        cancelAtPeriodEnd: true
      };
      
      setSubscription(updatedSubscription);
      localStorage.setItem('userSubscription', JSON.stringify(updatedSubscription));
      setError(null);
    } catch (err) {
      setError('Failed to cancel subscription');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const reactivateSubscription = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedSubscription: UserSubscription = {
        ...subscription!,
        cancelAtPeriodEnd: false,
        status: 'active'
      };
      
      setSubscription(updatedSubscription);
      localStorage.setItem('userSubscription', JSON.stringify(updatedSubscription));
      setError(null);
    } catch (err) {
      setError('Failed to reactivate subscription');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const startTrial = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const trialEnd = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
      
      const updatedSubscription: UserSubscription = {
        ...subscription!,
        tier: 'pro',
        status: 'trialing',
        trialEnd,
        currentPeriodEnd: trialEnd
      };
      
      setSubscription(updatedSubscription);
      localStorage.setItem('userSubscription', JSON.stringify(updatedSubscription));
      setError(null);
    } catch (err) {
      setError('Failed to start trial');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateBillingCycle = async (cycle: BillingCycle): Promise<void> => {
    try {
      setIsLoading(true);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedSubscription: UserSubscription = {
        ...subscription!,
        billingCycle: cycle
      };
      
      setSubscription(updatedSubscription);
      localStorage.setItem('userSubscription', JSON.stringify(updatedSubscription));
      setError(null);
    } catch (err) {
      setError('Failed to update billing cycle');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSubscription = async (): Promise<void> => {
    await loadSubscription();
  };

  // Utilities
  const getUpgradeUrl = (tier: SubscriptionTier): string => {
    return `/pricing?upgrade=${tier}`;
  };

  const showUpgradeModal = (feature?: string): void => {
    // Navigate to pricing page with feature parameter
    window.location.href = `/pricing${feature ? `?feature=${encodeURIComponent(feature)}` : ''}`;
  };

  // Track usage (helper function)
  const trackUsage = (metric: keyof UsageMetrics, increment: number = 1) => {
    const newUsage = {
      ...usage,
      [metric]: (usage[metric] || 0) + increment
    };
    setUsage(newUsage);
    localStorage.setItem('usageMetrics', JSON.stringify(newUsage));
  };

  // Expose trackUsage through window for easy access (development only)
  useEffect(() => {
    (window as any).trackUsage = trackUsage;
  }, [usage]);

  const value: SubscriptionContextType = {
    subscription,
    isLoading,
    error,
    currentTier,
    isActive,
    isTrial,
    trialInfo,
    canAccess,
    hasFeature,
    requiresTier,
    usage,
    isWithinLimit,
    getRemainingLimit,
    upgradeTo,
    downgradeTo,
    cancelSubscription,
    reactivateSubscription,
    startTrial,
    updateBillingCycle,
    getUpgradeUrl,
    showUpgradeModal,
    refreshSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription(): SubscriptionContextType {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

// Convenience hooks
export function useFeatureAccess(feature: keyof typeof SUBSCRIPTION_LIMITS.free.features): boolean {
  const { canAccess } = useSubscription();
  return canAccess(feature);
}

export function useTierCheck(requiredTier: SubscriptionTier): boolean {
  const { requiresTier } = useSubscription();
  return requiresTier(requiredTier);
}

export function useUsageLimit(limitType: string): {
  isWithinLimit: boolean;
  remaining: number | 'unlimited';
  percentage: number;
} {
  const { isWithinLimit, getRemainingLimit, currentTier } = useSubscription();
  const within = isWithinLimit(limitType);
  const remaining = getRemainingLimit(limitType);
  
  const limits = SUBSCRIPTION_LIMITS[currentTier];
  const limitValue = limits[limitType as keyof typeof limits];
  
  let percentage = 0;
  if (limitValue !== 'unlimited' && limitValue !== -1 && typeof remaining === 'number') {
    percentage = ((limitValue as number - remaining) / (limitValue as number)) * 100;
  }
  
  return { isWithinLimit: within, remaining, percentage };
}
